import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MessageCircleCode } from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../utils/apiConfig';
import '../board/toolbar/BoardClickUpFilterChip.css';
import '../board/toolbar/BoardToolbarSearchFilters.css';
import '../board/board-search-filters/BoardSearchFilters.css';
import './HubFeedback.css';
import HubTabs, { TAB_ROADMAP, TAB_SUESTOES } from './components/HubTabs';
import HubSidebar from './components/HubSidebar';
import HubSugestoesFilters from './components/HubSugestoesFilters';
import HubRoadmapFilters from './components/HubRoadmapFilters';
import SugestoesList from './components/SugestoesList';
import PostDetailView from './components/PostDetailView';
import RoadmapView from './components/RoadmapView';

const STATUS_LABELS = {
  open: 'Em aberto',
  under_review: 'Em análise',
  planned: 'Planejado',
  in_progress: 'Em andamento',
  complete: 'Concluído',
  closed: 'Cancelado'
};

const normalizeStatus = (s) => (s == null ? '' : String(s).toLowerCase().replace(/\s+/g, '_'));

const getApiBase = () => getApiBaseUrl();
const MAX_SUGGESTION_IMAGES = 3;
const MAX_SUGGESTION_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Página do Hub de Feedback: abas Roadmap e Sugestões (integração Canny).
 * Lista boards, posts por board e comentários por post via API (backend Canny).
 */
const HubFeedbackView = () => {
  const [activeTab, setActiveTab] = useState(TAB_SUESTOES);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [sugestaoInput, setSugestaoInput] = useState('');
  const [createTitle, setCreateTitle] = useState('');
  const [createDetails, setCreateDetails] = useState('');
  const [createCategory, setCreateCategory] = useState('');
  const [commentValue, setCommentValue] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyValue, setReplyValue] = useState('');

  const [boards, setBoards] = useState([]);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [postVoters, setPostVoters] = useState([]);
  const [postsRefreshTrigger, setPostsRefreshTrigger] = useState(0);
  const [sortBy, setSortBy] = useState('new');
  const [statusFilter, setStatusFilter] = useState('');
  const [listCategoryFilter, setListCategoryFilter] = useState('');
  const [onlyMySuggestions, setOnlyMySuggestions] = useState(false);
  const [roadmapFilterCategoryIds, setRoadmapFilterCategoryIds] = useState(null);
  const [votingPostId, setVotingPostId] = useState(null);
  const [createPostLoading, setCreatePostLoading] = useState(false);
  const [sendCommentLoading, setSendCommentLoading] = useState(false);
  const [sendReplyLoading, setSendReplyLoading] = useState(false);
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [attachmentsDraftToken, setAttachmentsDraftToken] = useState('');
  const [postFiles, setPostFiles] = useState([]);
  const fileInputRef = useRef(null);
  const uploadAbortRef = useRef(new Map());
  const prevBoardIdRef = useRef(null);

  const boardId = selectedBoardId || boards[0]?.id;
  const currentBoard = boards.find((b) => b.id === boardId) || boards[0];

  const sortedPosts = useMemo(() => {
    const list = [...(posts || [])];
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (sortBy === 'trending') {
      const inLast7 = list.filter((p) => p.created && (now - new Date(p.created).getTime() <= sevenDaysMs));
      inLast7.sort((a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0));
      return inLast7;
    }
    if (sortBy === 'top') {
      list.sort((a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0));
      return list;
    }
    list.sort((a, b) => {
      const tA = a.created ? new Date(a.created).getTime() : 0;
      const tB = b.created ? new Date(b.created).getTime() : 0;
      return tB - tA;
    });
    return list;
  }, [posts, sortBy]);

  const filteredPosts = useMemo(() => {
    let list = sortedPosts;
    if (statusFilter) list = list.filter((p) => normalizeStatus(p.status) === statusFilter);
    else list = list.filter((p) => normalizeStatus(p.status) !== 'complete');
    if (listCategoryFilter) list = list.filter((p) => (p.categoryID || p.categoryId) === listCategoryFilter);
    if (onlyMySuggestions) list = list.filter((p) => p.createdByMe === true);
    const search = (sugestaoInput || '').trim().toLowerCase();
    if (search) {
      list = list.filter((p) => {
        const title = (p.title || '').toLowerCase();
        const details = (p.details || p.description || '').toLowerCase();
        return title.includes(search) || details.includes(search);
      });
    }
    return list;
  }, [sortedPosts, statusFilter, listCategoryFilter, onlyMySuggestions, sugestaoInput]);

  useEffect(() => {
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${base}/api/canny/boards`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { boards: [] }))
      .then((data) => setBoards(data.boards || []))
      .catch(() => setBoards([]));
  }, []);

  useEffect(() => {
    if (!boardId) {
      setPosts([]);
      setCategories([]);
      prevBoardIdRef.current = null;
      return;
    }
    const boardChanged = prevBoardIdRef.current !== boardId;
    prevBoardIdRef.current = boardId;
    if (boardChanged) setPosts([]);
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${base}/api/canny/boards/${encodeURIComponent(boardId)}/posts`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((data) => setPosts(data.posts || []))
      .catch(() => { if (boardChanged) setPosts([]); });
    if (boardChanged) setCategories([]);
    fetch(`${base}/api/canny/boards/${encodeURIComponent(boardId)}/categories`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((data) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, [boardId, postsRefreshTrigger]);

  useEffect(() => {
    if (!selectedPost || !selectedPost.id) {
      setComments([]);
      setPostVoters([]);
      setReplyingToCommentId(null);
      setReplyValue('');
      return;
    }
    setReplyingToCommentId(null);
    setReplyValue('');
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    setComments([]);
    setPostVoters([]);
    const postId = selectedPost.id;
    fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { comments: [] }))
      .then((data) => setComments(data.comments || []))
      .catch(() => setComments([]));
    fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/voters`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { voters: [] }))
      .then((data) => setPostVoters(data.voters || []))
      .catch(() => setPostVoters([]));
  }, [selectedPost?.id]);

  useEffect(() => {
    if (!selectedPost?.id) {
      setPostFiles([]);
      return;
    }
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    let cancelled = false;
    fetch(`${base}/api/canny/posts/${encodeURIComponent(selectedPost.id)}/files`, { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : { files: [] }))
      .then((data) => {
        if (!cancelled) {
          setPostFiles(Array.isArray(data.files) ? data.files : []);
        }
      })
      .catch(() => {
        if (!cancelled) setPostFiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPost?.id]);

  const getStatusLabel = useCallback((status) => STATUS_LABELS[normalizeStatus(status)] || status, []);

  const handleSendComment = useCallback(() => {
    if (!selectedPost || !selectedPost.id || !commentValue.trim() || sendCommentLoading) return;
    setSendCommentLoading(true);
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const postId = selectedPost.id;
    fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ value: commentValue.trim() })
    })
      .then((r) => {
        if (r.ok) {
          setCommentValue('');
          return fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then((res) => (res.ok ? res.json() : { comments: [] }))
            .then((data) => setComments(data.comments || []))
            .catch(() => {});
        }
        return r.json().then((data) => alert(data.error || 'Erro ao enviar comentário')).catch(() => alert('Erro ao enviar comentário'));
      })
      .catch(() => alert('Erro ao enviar comentário'))
      .finally(() => setSendCommentLoading(false));
  }, [selectedPost, commentValue, sendCommentLoading]);

  const handleStartReply = useCallback((comment) => {
    setReplyingToCommentId(comment && comment.id ? comment.id : null);
    setReplyValue('');
  }, []);

  const handleSendReply = useCallback(() => {
    if (!selectedPost || !selectedPost.id || !replyingToCommentId || !replyValue.trim() || sendReplyLoading) return;
    setSendReplyLoading(true);
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const postId = selectedPost.id;
    fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ value: replyValue.trim(), parentCommentId: replyingToCommentId })
    })
      .then((r) => {
        if (r.ok) {
          setReplyingToCommentId(null);
          setReplyValue('');
          return fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then((res) => (res.ok ? res.json() : { comments: [] }))
            .then((data) => setComments(data.comments || []))
            .catch(() => {});
        }
        return r.json().then((data) => alert(data.error || 'Erro ao enviar resposta')).catch(() => alert('Erro ao enviar resposta'));
      })
      .catch(() => alert('Erro ao enviar resposta'))
      .finally(() => setSendReplyLoading(false));
  }, [selectedPost, replyingToCommentId, replyValue, sendReplyLoading]);

  const handleCancelReply = useCallback(() => {
    setReplyingToCommentId(null);
    setReplyValue('');
  }, []);

  const handleLikeComment = useCallback((comment) => {
    if (!selectedPost || !selectedPost.id || !comment || !comment.id) return;
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const postId = selectedPost.id;
    fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(comment.id)}/like`, {
      method: 'POST',
      credentials: 'include',
      headers
    })
      .then((r) => {
        if (r.ok) {
          return fetch(`${base}/api/canny/posts/${encodeURIComponent(postId)}/comments`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then((res) => (res.ok ? res.json() : { comments: [] }))
            .then((data) => setComments(data.comments || []))
            .catch(() => {});
        }
        if (r.status === 501) return;
        return r.json().then((data) => alert(data.error || 'Erro ao curtir')).catch(() => {});
      })
      .catch(() => {});
  }, [selectedPost]);

  const handleCreatePost = useCallback(() => {
    if (!boardId || !createTitle.trim() || createPostLoading) return;
    if (attachmentItems.some((item) => item.status === 'uploading')) {
      alert('Aguarde finalizar os uploads antes de criar a postagem.');
      return;
    }
    setCreatePostLoading(true);
    const base = getApiBase();
    const body = { boardID: boardId, title: createTitle.trim(), details: createDetails || '' };
    if (createCategory && createCategory.trim()) body.categoryID = createCategory.trim();
    if (attachmentsDraftToken) body.attachmentsDraftToken = attachmentsDraftToken;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${base}/api/canny/posts`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body)
    })
      .then((r) => {
        if (r.ok) {
          setShowCreateForm(false);
          setCreateTitle('');
          setCreateDetails('');
          setCreateCategory('');
          setAttachmentItems([]);
          setAttachmentsDraftToken('');
          setPostsRefreshTrigger((t) => t + 1);
        } else {
          r.json().then((data) => alert(data.error || 'Erro ao criar postagem')).catch(() => alert('Erro ao criar postagem'));
        }
      })
      .catch(() => alert('Erro ao criar postagem'))
      .finally(() => setCreatePostLoading(false));
  }, [boardId, createTitle, createDetails, createCategory, createPostLoading, attachmentsDraftToken, attachmentItems]);

  const handleOpenAttachPicker = useCallback(() => {
    if (createPostLoading) return;
    fileInputRef.current?.click();
  }, [createPostLoading]);

  const uploadViaXHR = useCallback((url, file, onProgress, signal) => (
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Falha no upload')));
      xhr.onerror = () => reject(new Error('Falha no upload'));
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('UPLOAD_ABORTED'));
      });
      xhr.send(file);
    })
  ), []);

  const fileSha256 = useCallback(async (file) => {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }, []);

  const handleAttachFiles = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    const currentCount = attachmentItems.filter((item) => item.status !== 'canceled').length;
    if (currentCount + files.length > MAX_SUGGESTION_IMAGES) {
      alert('Máximo de 3 imagens por sugestão.');
      return;
    }
    const invalid = files.find((file) => !String(file.type || '').toLowerCase().startsWith('image/'));
    if (invalid) {
      alert('Somente arquivos de imagem são permitidos.');
      return;
    }
    const invalidSize = files.find((file) => Number(file.size || 0) > MAX_SUGGESTION_IMAGE_SIZE_BYTES);
    if (invalidSize) {
      alert('Cada imagem deve ter no máximo 10MB.');
      return;
    }

    for (const file of files) {
      const localId = `${Date.now()}-${Math.random()}`;
      setAttachmentItems((prev) => [...prev, { localId, file, status: 'pending', progress: 0 }]);
      try {
        const checksumSha256 = await fileSha256(file);
        const base = getApiBase();
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const moduleName = currentBoard?.name || 'hubfeedback';
        const presignRes = await fetch(`${base}/api/canny/suggestions/uploads/presign`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            draftToken: attachmentsDraftToken || undefined,
            moduleName,
            originalName: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            checksumSha256
          })
        });
        const presignData = await presignRes.json();
        if (!presignRes.ok) throw new Error(presignData.error || 'Falha ao iniciar upload');
        if (!attachmentsDraftToken) setAttachmentsDraftToken(presignData.draftToken);
        const controller = new AbortController();
        uploadAbortRef.current.set(localId, { controller, fileId: presignData.fileId, draftToken: presignData.draftToken });
        setAttachmentItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, status: 'uploading', fileId: presignData.fileId, draftToken: presignData.draftToken } : item)));
        await uploadViaXHR(presignData.uploadUrl, file, (progress) => {
          setAttachmentItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, progress } : item)));
        }, controller.signal);
        await fetch(`${base}/api/canny/suggestions/uploads/${encodeURIComponent(presignData.fileId)}/complete`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ draftToken: presignData.draftToken })
        });
        setAttachmentItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, status: 'done', progress: 100 } : item)));
      } catch (error) {
        if (error.message !== 'UPLOAD_ABORTED') {
          setAttachmentItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, status: 'error' } : item)));
        }
      }
    }
  }, [attachmentItems, attachmentsDraftToken, currentBoard?.name, fileSha256, uploadViaXHR]);

  const handleCancelAttachment = useCallback((localId) => {
    const uploadRef = uploadAbortRef.current.get(localId);
    if (uploadRef) {
      uploadRef.controller.abort();
      const base = getApiBase();
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      fetch(`${base}/api/canny/suggestions/uploads/${encodeURIComponent(uploadRef.fileId)}?draftToken=${encodeURIComponent(uploadRef.draftToken || attachmentsDraftToken)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      }).catch(() => {});
      uploadAbortRef.current.delete(localId);
    }
    setAttachmentItems((prev) => prev.filter((item) => item.localId !== localId));
  }, [attachmentsDraftToken]);

  const handleVote = useCallback((post) => {
    if (!post || !post.id) return;
    const base = getApiBase();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const isRemoving = !!post.votedByMe;
    const url = `${base}/api/canny/posts/${encodeURIComponent(post.id)}/vote`;
    const nextVotedByMe = !isRemoving;
    const nextCount = Math.max(0, (post.voteCount ?? 0) + (isRemoving ? -1 : 1));
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, votedByMe: nextVotedByMe, voteCount: nextCount } : p)));
    setVotingPostId(post.id);
    fetch(url, {
      method: isRemoving ? 'DELETE' : 'POST',
      credentials: 'include',
      headers
    })
      .then((r) => {
        if (r.ok) {
          setPostsRefreshTrigger((t) => t + 1);
          if (selectedPost && selectedPost.id === post.id) {
            setSelectedPost((prev) => ({
              ...prev,
              votedByMe: nextVotedByMe,
              voteCount: nextCount
            }));
            fetch(`${base}/api/canny/posts/${encodeURIComponent(post.id)}/voters`, { credentials: 'include', headers })
              .then((res) => (res.ok ? res.json() : { voters: [] }))
              .then((data) => setPostVoters(data.voters || []))
              .catch(() => {});
          }
        } else {
          setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, votedByMe: post.votedByMe, voteCount: post.voteCount ?? 0 } : p)));
        }
      })
      .catch(() => {
        setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, votedByMe: post.votedByMe, voteCount: post.voteCount ?? 0 } : p)));
      })
      .finally(() => setVotingPostId(null));
  }, [selectedPost?.id]);

  const postsForRoadmap = useMemo(() => {
    return (posts || []).filter((p) => {
      const catId = p.categoryID || p.categoryId;
      const hasCategory = !!catId;
      const categorySelected = roadmapFilterCategoryIds == null || roadmapFilterCategoryIds.includes(catId);
      const mySuggestionsOk = !onlyMySuggestions || p.createdByMe === true;
      return hasCategory && categorySelected && mySuggestionsOk;
    });
  }, [posts, roadmapFilterCategoryIds, onlyMySuggestions]);

  const postsByStatus = useMemo(() => ({
    planned: postsForRoadmap.filter((p) => normalizeStatus(p.status) === 'planned'),
    in_progress: postsForRoadmap.filter((p) => normalizeStatus(p.status) === 'in_progress'),
    complete: postsForRoadmap.filter((p) => normalizeStatus(p.status) === 'complete')
  }), [postsForRoadmap]);

  return (
    <div className="hub-feedback-view">
      <header className="hub-feedback-header">
        <nav className="hub-feedback-breadcrumb" aria-label="Localização no hub de feedback">
          <span className="hub-feedback-breadcrumb-avatar" aria-hidden="true">
            H
          </span>
          <span className="hub-feedback-breadcrumb-segment">...</span>
          <span className="hub-feedback-breadcrumb-sep" aria-hidden="true">
            /
          </span>
          <span className="hub-feedback-breadcrumb-current" aria-current="page">
            <MessageCircleCode size={13} strokeWidth={1.75} aria-hidden className="hub-feedback-breadcrumb-current-icon" />
            <span className="hub-feedback-breadcrumb-current-text">Hub de Feedback</span>
          </span>
        </nav>
      </header>

      <div className="hub-feedback-toolbar">
        <HubTabs activeTab={activeTab} onTabChange={setActiveTab} />
        {activeTab === TAB_SUESTOES ? (
          <HubSugestoesFilters
            sortBy={sortBy}
            onSortChange={setSortBy}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            listCategoryFilter={listCategoryFilter}
            onListCategoryFilterChange={setListCategoryFilter}
            onlyMySuggestions={onlyMySuggestions}
            onOnlyMySuggestionsChange={setOnlyMySuggestions}
            categories={categories}
            getStatusLabel={getStatusLabel}
            searchValue={sugestaoInput}
            onSearchChange={setSugestaoInput}
            obscured={!!selectedPost}
          />
        ) : activeTab === TAB_ROADMAP ? (
          <HubRoadmapFilters
            categories={categories}
            filterCategoryIds={roadmapFilterCategoryIds}
            onFilterCategoryToggle={(categoryId) => {
              setRoadmapFilterCategoryIds((prev) => {
                const ids = prev == null ? (categories || []).map((c) => c.id) : [...prev];
                const i = ids.indexOf(categoryId);
                if (i === -1) ids.push(categoryId);
                else ids.splice(i, 1);
                return ids.length === (categories || []).length ? null : ids;
              });
            }}
            onFilterSelectNone={() => setRoadmapFilterCategoryIds([])}
            onFilterSelectAll={() => setRoadmapFilterCategoryIds(null)}
            onlyMySuggestions={onlyMySuggestions}
            onOnlyMySuggestionsChange={setOnlyMySuggestions}
          />
        ) : null}
      </div>

      <div className="hub-feedback-body">
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleAttachFiles} style={{ display: 'none' }} />
        <HubSidebar
          boards={boards}
          countKey={activeTab === TAB_ROADMAP ? 'roadmapCount' : 'postCount'}
          selectedBoardId={boardId}
          onSelectBoard={(id) => { setSelectedBoardId(id); setSelectedPost(null); setShowCreateForm(false); setCreateCategory(''); setStatusFilter(''); setListCategoryFilter(''); setOnlyMySuggestions(false); setRoadmapFilterCategoryIds(null); }}
        />
        {activeTab === TAB_ROADMAP ? (
          <RoadmapView
            boards={boards}
            selectedBoardId={selectedBoardId}
            onSelectBoard={setSelectedBoardId}
            postsByStatus={postsByStatus}
            onPostClick={(post) => { setSelectedPost(post); setActiveTab(TAB_SUESTOES); }}
            onVoteClick={handleVote}
            votingPostId={votingPostId}
            showSidebar={false}
          />
        ) : (
          <div className="hub-feedback-main">
              {selectedPost ? (
                <PostDetailView
                  post={selectedPost}
                  files={postFiles}
                  comments={comments}
                  commentValue={commentValue}
                  onCommentChange={setCommentValue}
                  onSendComment={handleSendComment}
                  sendCommentLoading={sendCommentLoading}
                  onVote={handleVote}
                  onLikeComment={handleLikeComment}
                  onBack={() => setSelectedPost(null)}
                  getStatusLabel={getStatusLabel}
                  replyingToCommentId={replyingToCommentId}
                  replyValue={replyValue}
                  onReplyChange={setReplyValue}
                  onStartReply={handleStartReply}
                  onSendReply={handleSendReply}
                  sendReplyLoading={sendReplyLoading}
                  onCancelReply={handleCancelReply}
                  voters={postVoters}
                />
              ) : (
                <SugestoesList
                  onNewSuggestionClick={() => setShowCreateForm(true)}
                  posts={filteredPosts}
                  onPostClick={setSelectedPost}
                  onVoteClick={handleVote}
                  votingPostId={votingPostId}
                  getStatusLabel={getStatusLabel}
                  expanded={showCreateForm}
                  createTitle={createTitle}
                  createDetails={createDetails}
                  createCategory={createCategory}
                  categories={categories}
                  onTitleChange={setCreateTitle}
                  onDetailsChange={setCreateDetails}
                  onCategoryChange={setCreateCategory}
                  onCancelCreate={() => { setShowCreateForm(false); setCreateTitle(''); setCreateDetails(''); setCreateCategory(''); setAttachmentItems([]); setAttachmentsDraftToken(''); }}
                  onCreatePost={handleCreatePost}
                  createPostLoading={createPostLoading}
                  attachmentItems={attachmentItems}
                  onOpenAttachPicker={handleOpenAttachPicker}
                  onCancelAttachment={handleCancelAttachment}
                  boardName={currentBoard?.name}
                />
              )}
            </div>
        )}
      </div>
    </div>
  );
};

export default HubFeedbackView;
